import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useMemo, useState, useEffect } from "react";
import {
  addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format,
  isSameDay, isSameMonth, parseISO, startOfMonth, startOfWeek
} from "date-fns";
import { de } from "date-fns/locale";
import { Link, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { apiGet } from "@/lib/api";
import type { EventDto } from "@/types";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { EVENT_CATEGORIES, normaliseEventCategory } from "@/lib/eventCategories";

type View = "month" | "list";

/** Fixed palette - admin can later override the corresponding theme tokens. */
function categoryBg(raw: string | undefined): string {
  const c = EVENT_CATEGORIES.find((x) => x.key === normaliseEventCategory(raw));
  return c?.colorBg ?? "#E5E5E5";
}
function categoryText(raw: string | undefined): string {
  const c = EVENT_CATEGORIES.find((x) => x.key === normaliseEventCategory(raw));
  return c?.colorText ?? "#444444";
}

/**
 * Public calendar page. Phase 3 changes:
 *  - Days with events get a soft tinted background (spec 3).
 *  - Each event chip is coloured by its category (Information / Veranstaltung / Ereignis).
 *  - "Heute" / "Naechster Monat" controls were already there; cleaned up.
 *  - Added "ICS-Export" link to the whole calendar.
 *  - URL `?date=YYYY-MM-DD` (used by sidebar mini-calendar) opens that month
 *    and highlights the day.
 */
export function CalendarPage() {
  const locale = de;
  const [params] = useSearchParams();

  const initialDate = useMemo(() => {
    const d = params.get("date");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return parseISO(d);
    return new Date();
  }, [params]);

  const [cursor, setCursor] = useState<Date>(initialDate);
  const [highlight, setHighlight] = useState<Date | null>(initialDate);
  const [view, setView] = useState<View>(
    typeof window !== "undefined" && window.innerWidth < 768 ? "list" : "month",
  );

  // Keep cursor in sync when URL ?date= changes (e.g. sidebar click).
  useEffect(() => {
    setCursor(initialDate);
    setHighlight(initialDate);
  }, [initialDate]);

  const from = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const to = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });

  const { data: events = [] } = useQuery({
    queryKey: ["events", "de", from.toISOString(), to.toISOString()],
    queryFn: () => apiGet<EventDto[]>(
      `/public/events?locale=${"de"}&from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
    ),
  });

  const days = useMemo(() => eachDayOfInterval({ start: from, end: to }), [from, to]);
  const eventsByDay = useMemo(() => {
    const map = new Map<string, EventDto[]>();
    for (const e of events) {
      const key = format(parseISO(e.startAt), "yyyy-MM-dd");
      const arr = map.get(key) ?? [];
      arr.push(e);
      map.set(key, arr);
    }
    return map;
  }, [events]);

  return (
    <>
      <Helmet><title>{"Kalender"} | Falkenrehde</title></Helmet>
      <Breadcrumb items={[{ label: "Kalender" }]} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-primary">
          {"Kalender"}
        </h1>
        <div className="flex items-center gap-2">
          <a
            href="/api/v1/public/events/calendar.ics"
            className="text-sm text-primary hover:underline"
            title="Gesamtkalender als ICS-Datei herunterladen"
          >
            ICS-Export
          </a>
          <div className="flex gap-1 bg-bg-muted rounded-md p-1">
            <button
              onClick={() => setView("month")}
              className={clsx("px-3 py-1.5 text-sm rounded", view === "month" ? "bg-bg shadow-sm" : "text-text-muted")}
              aria-pressed={view === "month"}
            >{"Monat"}</button>
            <button
              onClick={() => setView("list")}
              className={clsx("px-3 py-1.5 text-sm rounded", view === "list" ? "bg-bg shadow-sm" : "text-text-muted")}
              aria-pressed={view === "list"}
            >{"Liste"}</button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCursor(addMonths(cursor, -1))}
          className="px-3 py-1.5 border border-border rounded-md hover:bg-bg-muted"
          aria-label="Vorheriger Monat"
        >&lsaquo;</button>
        <div className="font-serif font-bold text-lg text-primary">
          {format(cursor, "LLLL yyyy", { locale })}
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setCursor(new Date())}
            className="px-3 py-1.5 border border-border rounded-md hover:bg-bg-muted text-sm"
          >{"Heute"}</button>
          <button
            onClick={() => setCursor(addMonths(cursor, 1))}
            className="px-3 py-1.5 border border-border rounded-md hover:bg-bg-muted"
            aria-label="Naechster Monat"
          >&rsaquo;</button>
        </div>
      </div>

      <CategoryLegendBar />

      {view === "month" ? (
        <div className="border border-border rounded-lg overflow-hidden bg-bg">
          <div className="grid grid-cols-7 bg-bg-muted text-xs font-medium text-text-muted">
            {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => (
              <div key={d} className="p-2 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayEvents = eventsByDay.get(key) ?? [];
              const inMonth = isSameMonth(day, cursor);
              const today = isSameDay(day, new Date());
              const isHi = highlight !== null && isSameDay(day, highlight);
              const hasEvents = dayEvents.length > 0;
              return (
                <div
                  key={key}
                  className={clsx(
                    "min-h-[88px] p-1.5 border-t border-r border-border text-sm transition-colors",
                    !inMonth && "bg-bg-muted/50 text-text-muted",
                    inMonth && hasEvents && "bg-primary-soft/60",
                    isHi && "ring-2 ring-accent ring-inset",
                  )}
                >
                  <div className={clsx(
                    "inline-flex items-center justify-center w-6 h-6 rounded-full text-xs",
                    today ? "bg-primary text-white font-bold" : "text-text",
                  )}>
                    {format(day, "d")}
                  </div>
                  <div className="mt-1 space-y-1">
                    {dayEvents.slice(0, 3).map((e) => {
                      const color =
                        categoryBg(e.category);
                      return (
                        <Link
                          key={e.id}
                          to={`/kalender/${e.slug}`}
                          className="block truncate text-xs px-1.5 py-0.5 rounded text-white hover:opacity-90 transition-opacity"
                          style={{ background: color }}
                          title={e.title}
                        >
                          {e.title}
                        </Link>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-text-muted">+{dayEvents.length - 3}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-lg bg-bg">
          {events.length === 0 && <li className="p-4 text-text-muted italic">{"Keine Termine in diesem Zeitraum."}</li>}
          {events.map((e) => {
            const color = categoryBg(e.category);
            return (
              <li key={e.id} className="p-4 flex items-center gap-4 hover:bg-bg-muted">
                <span
                  aria-hidden
                  className="block w-1 self-stretch rounded-sm"
                  style={{ background: color }}
                />
                <div className="text-center min-w-[64px]">
                  <div className="text-xs uppercase text-text-muted">{format(parseISO(e.startAt), "MMM", { locale })}</div>
                  <div className="text-xl font-bold text-primary font-serif">{format(parseISO(e.startAt), "d", { locale })}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/kalender/${e.slug}`} className="font-medium hover:text-primary block">
                    {e.title}
                  </Link>
                  {e.location && <div className="text-sm text-text-muted truncate">{e.location}</div>}
                </div>
                <span
                  className="hidden sm:inline text-xs uppercase tracking-wide px-2 py-1 rounded-full text-white"
                  style={{ background: color }}
                >
                  {e.category}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function CategoryLegendBar() {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
      <span className="uppercase tracking-wide text-text-muted">Kategorien:</span>
      {EVENT_CATEGORIES.map((c) => (
        <span key={c.key}
              className="inline-flex items-center px-2 py-0.5 rounded-full"
              style={{ background: c.colorBg, color: c.colorText }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}
